// Axios instance for /api/v1/tasks. Same JWT source as the CRM client
// (localStorage 'token'). Adds one 429 retry that honours `retryAfter` and
// publishes the wait to the UI store so RateLimitBanner can count down
// "Slow down — retrying in n s".
import axios from "axios";
import { useUI } from "../store/ui";

export const api = axios.create({ baseURL: "/api/v1/tasks", timeout: 20000 });

api.interceptors.request.use((config) => {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === "function") config.headers.delete("Content-Type");
    else if (config.headers) delete config.headers["Content-Type"];
  }
  return config;
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const res = err.response;
    const cfg = err.config || {};
    if (res && res.status === 429 && !cfg.__retried) {
      const secs = Number(res.data?.retryAfter) || Math.max(1, Math.ceil((Number(res.headers?.["x-ratelimit-reset"]) - Date.now()) / 1000)) || 2;
      const wait = Math.min(secs, 30) * 1000;
      useUI.getState().setRateLimit(Date.now() + wait);
      await sleep(wait);
      useUI.getState().setRateLimit(0);
      return api({ ...cfg, __retried: true });
    }
    return Promise.reject(err);
  }
);

// Unwrap the envelope: { success, data, meta } → data. Errors are normalised
// to { code, message, status } so organisms can render a real sentence.
export async function call(promise) {
  try {
    const res = await promise;
    const body = res.data;
    if (body && body.success === false) throw normalise({ response: { status: res.status, data: body } });
    return body && Object.prototype.hasOwnProperty.call(body, "data") ? body.data : body;
  } catch (e) {
    throw normalise(e);
  }
}

export function normalise(e) {
  if (e && e.code && e.message && e.status !== undefined) return e;
  const res = e?.response;
  const out = new Error(res?.data?.message || (res?.status === 404 && res?.data?.code === "tasks_not_enabled" ? "Tasks is not enabled for this organisation." : e?.message || "Something went wrong"));
  out.code = res?.data?.code || (res ? `http_${res.status}` : "network");
  out.status = res?.status ?? 0;
  out.details = res?.data?.details;
  return out;
}

export const get = (url, params, config) => call(api.get(url, { params, ...config }));
export const post = (url, body, config) => call(api.post(url, body, config));
export const put = (url, body, config) => call(api.put(url, body, config));
export const patch = (url, body, config) => call(api.patch(url, body, config));
export const del = (url, config) => call(api.delete(url, config));
